/**
 * Application shell: tab routing, the status bar, and ownership of which
 * signals are being polled.
 *
 * Only the visible tab drives subscriptions. Bus bandwidth is the scarce
 * resource -- polling a dashboard nobody is looking at directly slows down the
 * one they are.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import {
  LEARNED_PARTS, compareIdentities, identityKey, isMismatch,
  type IdentityMatch, type LearnedPart, type LearnedState, type ObdSession, type VehicleIdentity,
} from '@obd/core';
import type { LearnedOwners, WidgetType } from './store/appState';
import { applyCarProfile, buildCarProfile, hasLearned } from './store/carProfile';
import { useCarRecords } from './store/records';
import type { ProfileActions } from './ui/ProfileCard';
import {
  loadState, saveState, subscriptionsFor, uid,
  type AppSettings, type Dashboard, type PersistedState,
} from './store/appState';
import {
  SessionProvider, enterDriveMode, exitDriveMode, useInstallPrompt, useSession,
  useSignalStructure, useWakeLock,
} from './store/session';
import { DashboardView, Modal } from './ui/DashboardView';
import { SignalsView } from './ui/SignalsView';
import { DiagnosticsView } from './ui/DiagnosticsView';
import { TripView } from './ui/TripView';
import { SetupView } from './ui/SetupView';
import { theme, useLiveText } from './ui/render';

type Tab = 'dashboards' | 'signals' | 'diagnostics' | 'trip' | 'setup';

const TABS: { id: Tab; label: string }[] = [
  { id: 'dashboards', label: 'Dashboards' },
  { id: 'signals', label: 'Signals' },
  { id: 'trip', label: 'Trip' },
  { id: 'diagnostics', label: 'Diagnostics' },
  { id: 'setup', label: 'Setup' },
];

export default function App(): JSX.Element {
  const [state, setState] = useState<PersistedState>(() => loadState());

  useEffect(() => {
    saveState(state);
  }, [state]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', state.settings.theme);
    // Painted colours are cached for the frame loop, so they must be re-read.
    theme.invalidate();
  }, [state.settings.theme]);

  return (
    <SessionProvider
      vehicle={state.settings.vehicle}
      autoReconnect={state.settings.autoReconnect}
      autoConnect={state.settings.autoConnect}
      gpsEnabled={state.settings.gpsEnabled}
      onVehicleChange={(v) => setState((s) => ({ ...s, settings: { ...s.settings, vehicle: v } }))}
    >
      <Shell state={state} setState={setState} />
    </SessionProvider>
  );
}

function Shell({
  state, setState,
}: {
  state: PersistedState;
  setState: React.Dispatch<React.SetStateAction<PersistedState>>;
}): JSX.Element {
  const { session, status, reconnectAttempt, gps, simulator, lastTrip } = useSession();
  const structureVersion = useSignalStructure();
  const [tab, setTab] = useState<Tab>('dashboards');
  const [editing, setEditing] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [driveMode, setDriveMode] = useState(false);

  const connected = status.state === 'connected';
  // Keep the screen alive in drive mode even before the adapter is up, so the
  // phone does not sleep while you are still getting connected in the car.
  useWakeLock((state.settings.keepAwake && connected) || driveMode);

  const settings = state.settings;
  const learnedStore = useLearnedPersistence(session, state, setState, simulator !== null);

  // ------------------------------------------------------------ which car
  //
  // The car connected now -- the simulator is not a car -- and the one this
  // device was last used with. Calibration records which car it was learned
  // on, and a mismatch only ever produces a warning.
  const liveCar = connected && !simulator ? status.identity : undefined;
  const liveCarKey = identityKey(liveCar);
  const thisCar = liveCar ?? state.car;
  const [dismissedCar, setDismissedCar] = useState('');

  const setOwner = useCallback(
    (part: LearnedPart, owner: VehicleIdentity | undefined) => {
      setState((s) => {
        const owners: LearnedOwners = { ...s.learnedFrom };
        if (owner) owners[part] = owner;
        else delete owners[part];
        return { ...s, learnedFrom: owners };
      });
    },
    [setState],
  );

  // A newly identified car: remember it, and let it own any calibration that
  // has no owner yet, or that it evidently owns already.
  useEffect(() => {
    if (!liveCar) return;
    const learned = learnedStore.current();
    setState((s) => {
      const owners: LearnedOwners = { ...s.learnedFrom };
      for (const part of LEARNED_PARTS) {
        const verdict = compareIdentities(owners[part], liveCar).verdict;
        if (!hasLearned(learned, part) || !owners[part] || verdict === 'same' || verdict === 'probably-same') {
          owners[part] = liveCar;
        }
      }
      return { ...s, car: liveCar, learnedFrom: owners };
    });
    // Runs once per car identified, not on every status refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveCarKey]);

  // Totals, fill-ups and the trip log for whichever car this is.
  const records = useCarRecords({
    session,
    car: thisCar,
    simulating: simulator !== null,
    lastTrip,
    onCalibrationChange: learnedStore.saveNow,
  });

  const carWarnings = useMemo(() => {
    if (!liveCar) return [];
    return LEARNED_PARTS
      .filter((part) => hasLearned(state.learned, part))
      .map((part) => ({ part, match: compareIdentities(state.learnedFrom?.[part], liveCar) }))
      .filter((w): w is { part: LearnedPart; match: IdentityMatch } => isMismatch(w.match));
  }, [liveCar, state.learned, state.learnedFrom]);

  const profileActions = useMemo<ProfileActions>(
    () => ({
      build: (includeDashboards) =>
        buildCarProfile({
          vehicle: state.settings.vehicle,
          learned: learnedStore.current(),
          car: thisCar,
          learnedFrom: state.learnedFrom,
          dashboards: includeDashboards ? { list: state.dashboards, activeId: state.activeDashboardId } : undefined,
        }),
      apply: (profile, parts) => {
        const result = applyCarProfile(state, learnedStore.current(), profile, parts);
        let next = result.state;
        if (result.learned) next = { ...next, learned: learnedStore.replace(result.learned) };
        if (parts.has('vehicle')) session.setVehicle(next.settings.vehicle);
        setState(next);
      },
      thisCar,
      thisCarLive: liveCar !== undefined,
      simulating: simulator !== null,
    }),
    [state, learnedStore, session, setState, thisCar, liveCar, simulator],
  );

  const toggleDriveMode = useCallback(() => {
    setDriveMode((on) => {
      const next = !on;
      if (next) {
        setEditing(false);
        setTab('dashboards');
        if (settings.fullscreenInDriveMode) void enterDriveMode(settings.orientationLock);
        else if (settings.orientationLock !== 'auto') void enterDriveMode(settings.orientationLock);
      } else {
        void exitDriveMode();
      }
      return next;
    });
  }, [settings.fullscreenInDriveMode, settings.orientationLock]);

  // Leaving fullscreen by the system back gesture should leave drive mode too,
  // otherwise the app is stuck with its navigation hidden.
  useEffect(() => {
    const onChange = (): void => {
      if (!document.fullscreenElement && driveMode && settings.fullscreenInDriveMode) setDriveMode(false);
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, [driveMode, settings.fullscreenInDriveMode]);

  const active = useMemo(
    () => state.dashboards.find((d) => d.id === state.activeDashboardId) ?? state.dashboards[0],
    [state.dashboards, state.activeDashboardId],
  );

  // The visible tab owns the subscription set.
  useEffect(() => {
    if (!connected) return;
    if (tab === 'dashboards') session.setSubscriptions(subscriptionsFor(active));
    // Signals, Trip and Setup manage their own; Diagnostics needs nothing live.
    if (tab === 'diagnostics') session.setSubscriptions([]);
  }, [tab, active, session, connected]);

  const updateSettings = useCallback(
    (patch: Partial<AppSettings>) => {
      setState((s) => ({ ...s, settings: { ...s.settings, ...patch } }));
    },
    [setState],
  );

  const updateDashboard = useCallback(
    (next: Dashboard) => {
      setState((s) => ({ ...s, dashboards: s.dashboards.map((d) => (d.id === next.id ? next : d)) }));
    },
    [setState],
  );

  const addDashboard = useCallback(() => {
    const board: Dashboard = { id: uid(), name: 'New dashboard', columns: 12, widgets: [] };
    setState((s) => ({ ...s, dashboards: [...s.dashboards, board], activeDashboardId: board.id }));
    setEditing(true);
  }, [setState]);

  const deleteDashboard = useCallback(() => {
    setState((s) => {
      if (s.dashboards.length <= 1) return s;
      const remaining = s.dashboards.filter((d) => d.id !== s.activeDashboardId);
      return { ...s, dashboards: remaining, activeDashboardId: remaining[0]?.id ?? '' };
    });
  }, [setState]);

  /** Send a signal from the Signals tab straight onto the active dashboard. */
  const addToDashboard = useCallback(
    (signalKey: string, type: WidgetType) => {
      setState((s) => {
        const board = s.dashboards.find((d) => d.id === s.activeDashboardId) ?? s.dashboards[0];
        if (!board) return s;
        const y = board.widgets.reduce((max, w) => Math.max(max, w.y + w.h), 0);
        const size = type === 'line' ? { w: 12, h: 4 }
          : type === 'gauge' ? { w: 4, h: 4 }
            : type === 'bar' || type === 'light' ? { w: 4, h: 1 }
              : { w: 3, h: 2 };
        const next: Dashboard = {
          ...board,
          widgets: [...board.widgets, {
            id: uid(), type, signals: [signalKey], x: 0, y,
            w: size.w, h: size.h, intervalMs: 250, sparkline: type === 'number',
          }],
        };
        return { ...s, dashboards: s.dashboards.map((d) => (d.id === next.id ? next : d)) };
      });
      setTab('dashboards');
    },
    [setState],
  );

  const cycleDashboard = (dir: 1 | -1): void => {
    setState((s) => {
      const i = s.dashboards.findIndex((d) => d.id === s.activeDashboardId);
      const next = s.dashboards[(i + dir + s.dashboards.length) % s.dashboards.length];
      return next ? { ...s, activeDashboardId: next.id } : s;
    });
  };

  return (
    <div className={'app' + (driveMode ? ' is-drive' : '')}>
      <header className="statusbar">
        <span className="brand">Telemetry</span>
        <span className="conn-dot" data-state={status.state} aria-hidden />
        <span className="muted">{connectionLabel(status.state, status.detail)}</span>
        <span className="spacer" />
        {gps.watching && <GpsBadge session={session} />}
        {connected && (
          <div className="metrics">
            <LiveMetric label="req/s" get={() => session.status().scheduler.requestsPerSec.toFixed(1)} />
            <LiveMetric label="val/s" get={() => session.status().scheduler.valuesPerSec.toFixed(0)} />
            <LiveMetric label="ms" get={() => session.status().scheduler.latencyMs.toFixed(0)} />
            <span>{status.adapter?.supportsBatch ? 'batched' : 'serial'}</span>
          </div>
        )}
        <button
          className="btn"
          data-size="sm"
          onClick={() => updateSettings({ theme: state.settings.theme === 'dark' ? 'light' : 'dark' })}
          title="Toggle theme"
        >
          {state.settings.theme === 'dark' ? '☾' : '☀'}
        </button>
        <button className="btn" data-size="sm" data-variant="primary" onClick={toggleDriveMode} title="Full-screen dashboard">
          Drive
        </button>
      </header>

      {carWarnings.length > 0 && dismissedCar !== liveCarKey && !driveMode && (
        <div className="car-banner" role="alert">
          <span>
            <strong>Different car?</strong> The {listText(carWarnings.map((w) => PART_NAMES[w.part]))}{' '}
            {carWarnings.length === 1 ? 'was' : 'were'} learned on another car. {carWarnings[0]?.match.reason} Readings
            that use {carWarnings.length === 1 ? 'it' : 'them'} may be off.
          </span>
          <span className="car-banner-actions">
            <button className="btn" data-size="sm" onClick={() => setTab('setup')}>Review</button>
            <button className="btn" data-size="sm" onClick={() => setDismissedCar(liveCarKey)}>Dismiss</button>
          </span>
        </div>
      )}

      {reconnectAttempt > 0 && (
        <div className="reconnect-banner">
          Adapter dropped — reconnecting (attempt {reconnectAttempt})…
        </div>
      )}

      <nav className="tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            className="tab"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {tab === 'dashboards' && (
        <div className="subbar">
          <select
            value={state.activeDashboardId}
            onChange={(e) => setState((s) => ({ ...s, activeDashboardId: e.target.value }))}
          >
            {state.dashboards.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          <button className="btn" data-size="sm" onClick={() => setEditing((v) => !v)}>
            {editing ? 'Done' : 'Edit'}
          </button>
          {editing && (
            <>
              <button className="btn" data-size="sm" onClick={() => setRenaming(true)}>Rename</button>
              <button className="btn" data-size="sm" onClick={addDashboard}>New</button>
              <label className="field-inline" title="Scale rows so the whole dashboard fits without scrolling">
                <input
                  type="checkbox"
                  checked={active?.fit ?? false}
                  onChange={(e) => active && updateDashboard({ ...active, fit: e.target.checked })}
                />
                <span className="muted">Fit screen</span>
              </label>
              <button
                className="btn"
                data-size="sm"
                data-variant="danger"
                disabled={state.dashboards.length <= 1}
                onClick={deleteDashboard}
              >
                Delete
              </button>
            </>
          )}
          <span className="spacer" />
          {!connected && <span className="muted small">Not connected — open Setup to connect or start the simulator.</span>}
        </div>
      )}

      <main className="view">
        {tab === 'dashboards' && active && (
          <DashboardView dashboard={active} session={session} editing={editing} onChange={updateDashboard} />
        )}
        {tab === 'signals' && (
          <SignalsView
            session={session}
            structureVersion={structureVersion}
            defaultIntervalMs={state.settings.signalsIntervalMs}
            onDefaultIntervalChange={(ms) => updateSettings({ signalsIntervalMs: ms })}
            sparklinePoints={state.settings.sparklinePoints}
            onSparklinePointsChange={(n) => updateSettings({ sparklinePoints: n })}
            onAddToDashboard={addToDashboard}
            dashboardName={active?.name ?? 'dashboard'}
          />
        )}
        {tab === 'trip' && <TripView session={session} connected={connected} records={records} />}
        {tab === 'diagnostics' && <DiagnosticsView session={session} connected={connected} />}
        {tab === 'setup' && (
          <SetupView
            settings={state.settings}
            onSettings={updateSettings}
            onLearnedChange={learnedStore.saveNow}
            profile={profileActions}
            records={records}
            provenance={{
              owners: state.learnedFrom ?? {},
              liveCar,
              onSetOwner: setOwner,
            }}
          />
        )}
      </main>

      {driveMode && (
        <div className="drive-controls">
          <button className="drive-btn" onClick={() => cycleDashboard(-1)} aria-label="Previous dashboard">‹</button>
          <span className="drive-name">{active?.name}</span>
          <button className="drive-btn" onClick={() => cycleDashboard(1)} aria-label="Next dashboard">›</button>
          <button className="drive-btn" onClick={toggleDriveMode} aria-label="Exit drive mode">✕</button>
        </div>
      )}

      {settings.dimLevel > 0 && (
        <div className="dim-overlay" style={{ opacity: settings.dimLevel }} aria-hidden />
      )}

      {renaming && active && (
        <Modal title="Rename dashboard" onClose={() => setRenaming(false)}>
          <label className="field">
            <span>Name</span>
            <input
              type="text"
              autoFocus
              value={active.name}
              onChange={(e) => updateDashboard({ ...active, name: e.target.value })}
            />
          </label>
          <div className="modal-actions">
            <button className="btn" data-variant="primary" onClick={() => setRenaming(false)}>Done</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/**
 * Keep what the session learns about the car -- gear ratios, accelerator
 * calibration, speed correction -- across restarts.
 *
 * Learning changes continuously while driving, but almost none of it matters
 * to a save, so the snapshot is compared on a coarse signature and written only
 * when that moves. Anything newer is also written as the page is hidden, which
 * on a phone may be the last chance before it is frozen.
 *
 * The simulator is a different car. While it runs, learning starts from
 * nothing and is never saved, and the real car's knowledge comes back after.
 */
function useLearnedPersistence(
  session: ObdSession,
  state: PersistedState,
  setState: React.Dispatch<React.SetStateAction<PersistedState>>,
  simulating: boolean,
): LearnedStore {
  const stateRef = useRef(state);
  stateRef.current = state;
  const simulatingRef = useRef(simulating);
  simulatingRef.current = simulating;
  const savedSignature = useRef('');
  const savedJson = useRef('');
  // Calibration lives in the session, outside React, so swapping it in has to
  // ask for a render itself -- or Setup keeps showing the old one.
  const [, rerender] = useReducer((n: number) => n + 1, 0);

  const write = useCallback(
    (force: boolean) => {
      const learned = session.derived.exportLearned();
      const signature = learnedSignature(learned);
      const json = JSON.stringify(learned);
      if (json === savedJson.current || (!force && signature === savedSignature.current)) return;
      savedSignature.current = signature;
      savedJson.current = json;
      // Written straight away as well: a render may not get to run first.
      saveState({ ...stateRef.current, learned });
      stateRef.current = { ...stateRef.current, learned };
      setState((s) => ({ ...s, learned }));
    },
    [session, setState],
  );

  const save = useCallback(
    (force: boolean) => {
      if (!simulatingRef.current) write(force);
    },
    [write],
  );

  useEffect(() => {
    session.derived.importLearned(simulating ? undefined : stateRef.current.learned);
    const snapshot = session.derived.exportLearned();
    savedSignature.current = learnedSignature(snapshot);
    savedJson.current = JSON.stringify(snapshot);
    rerender();
    // Leaving the real car for the simulator: keep what it learned since the last save.
    return () => {
      if (!simulating) write(true);
    };
  }, [session, simulating, write]);

  useEffect(() => {
    const id = setInterval(() => save(false), 5000);
    const onHide = (): void => {
      if (document.visibilityState === 'hidden') save(true);
    };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', onHide);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', onHide);
    };
  }, [save]);

  const saveNow = useCallback(() => save(true), [save]);

  const current = useCallback(
    (): LearnedState => (simulatingRef.current ? stateRef.current.learned ?? {} : session.derived.exportLearned()),
    [session],
  );

  const replace = useCallback(
    (learned: LearnedState): LearnedState => {
      if (simulatingRef.current) {
        // Picked up by the import above once the simulator stops.
        stateRef.current = { ...stateRef.current, learned };
        return learned;
      }
      session.derived.importLearned(learned);
      const snapshot = session.derived.exportLearned();
      savedSignature.current = learnedSignature(snapshot);
      savedJson.current = JSON.stringify(snapshot);
      stateRef.current = { ...stateRef.current, learned: snapshot };
      return snapshot;
    },
    [session],
  );

  return useMemo(() => ({ saveNow, current, replace }), [saveNow, current, replace]);
}

interface LearnedStore {
  /** Save straight away rather than on the next check, e.g. after a change by hand. */
  saveNow: () => void;
  /** The real car's calibration: live from the session, or as stored while the simulator runs. */
  current: () => LearnedState;
  /** Replace the real car's calibration. Returns what to store; the caller stores it. */
  replace: (learned: LearnedState) => LearnedState;
}

const PART_NAMES: Record<LearnedPart, string> = {
  gears: 'gear ratios',
  accelerator: 'accelerator calibration',
  speed: 'speed correction',
  fuel: 'fuel correction',
};

function listText(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return items.slice(0, -1).join(', ') + ' and ' + items[items.length - 1];
}

/** Only the parts of a learned snapshot whose change is worth a write. */
function learnedSignature(l: LearnedState): string {
  return JSON.stringify({
    gears: (l.gears ?? []).map((g) => [
      Math.round(Math.log(g.ratio) * 200),
      g.launchS > 0,
      Math.round(Math.log2(1 + g.dwellS)),
    ]),
    accelerator: (l.accelerator ?? []).map((a) => [
      a.source, Math.round(a.rest * 2), Math.round(a.floor * 2), a.pinnedRest, a.pinnedFull,
    ]),
    speed: l.speed && l.speed.obd > 0
      ? [Math.round((l.speed.gps / l.speed.obd) * 1000), Math.round(Math.log2(1 + l.speed.seconds))]
      : null,
  });
}

/**
 * Status-bar indicator for the phone's position fix. A phone standing still
 * may go quiet for a while, so a fix counts as current for some seconds.
 */
function GpsBadge({ session }: { session: ObdSession }): JSX.Element {
  const ref = useLiveText<HTMLSpanElement>(() => {
    const accuracy = session.store.get('gps_accuracy');
    const fresh = accuracy?.value !== undefined && Date.now() - accuracy.stats.lastUpdate < 15_000;
    return fresh ? 'GPS ±' + Math.round(accuracy.value ?? 0) + ' m' : 'GPS …';
  }, 1000);
  return <span className="pill gps-badge" ref={ref} title="Phone location">GPS …</span>;
}

function LiveMetric({ label, get }: { label: string; get: () => string }): JSX.Element {
  const ref = useLiveText<HTMLSpanElement>(() => get() + ' ' + label, 500);
  return <span ref={ref}>—</span>;
}

function connectionLabel(state: string, detail?: string): string {
  switch (state) {
    case 'connected': return 'Connected';
    case 'connecting': return detail ?? 'Connecting…';
    case 'handshaking': return 'Configuring adapter…';
    case 'identifying': return 'Reading vehicle capabilities…';
    case 'error': return detail ?? 'Connection error';
    default: return 'Disconnected';
  }
}
